import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';

const StatsCard = ({ 
  title, 
  value, 
  icon, 
  color = 'primary', 
  subtitle,
  trend,
  compact = false,
  onClick
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = compact || isMobile;

  return (
    <Card 
      onClick={onClick}
      sx={{ 
        height: '100%', 
        position: 'relative', 
        overflow: 'visible',
        borderRadius: 2,
        boxShadow: theme.shadows[3],
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: theme.shadows[6],
          transform: onClick ? 'translateY(-4px)' : 'translateY(-2px)',
        },
        transition: 'all 0.3s ease-in-out',
        background: `linear-gradient(135deg, ${theme.palette[color].light}15 0%, ${theme.palette[color].main}10 100%)`,
        border: `1px solid ${theme.palette[color].light}`,
      }}
    >
      <CardContent sx={{ pb: isCompact ? 1.5 : 2, p: isCompact ? 2 : 3 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: isCompact ? 'flex-start' : 'center', 
            mb: isCompact ? 1 : 1.5,
            flexDirection: isCompact ? 'column' : 'row'
          }}
        >
          <Avatar
            sx={{
              bgcolor: `${color}.main`,
              width: isCompact ? 40 : 56,
              height: isCompact ? 40 : 56,
              mr: isCompact ? 0 : 2,
              mb: isCompact ? 1 : 0,
              boxShadow: `0 4px 8px ${theme.palette[color].main}40`,
              alignSelf: isCompact ? 'center' : 'auto',
            }}
          >
            {React.cloneElement(icon, { 
              sx: { 
                fontSize: isCompact ? '1.25rem' : '1.5rem',
                color: 'white'
              } 
            })}
          </Avatar>
          
          <Box 
            sx={{ 
              flex: 1,
              textAlign: isCompact ? 'center' : 'left',
              alignSelf: 'center'
            }}
          >
            <Typography 
              variant={isCompact ? "h5" : "h4"} 
              component="div" 
              fontWeight="bold"
              sx={{
                color: `${color}.main`,
                lineHeight: 1.2,
                mb: 0.5
              }}
            >
              {value}
            </Typography>
            {trend && (
              <Typography
                variant="body2"
                color={trend.type === 'increase' ? 'success.main' : 'error.main'}
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: isCompact ? '0.75rem' : '0.875rem'
                }}
              >
                {trend.type === 'increase' ? '↗ +' : '↘ '}{trend.value}
              </Typography>
            )}
          </Box>
        </Box>
        
        <Typography 
          variant={isCompact ? "body1" : "h6"} 
          color="text.primary" 
          sx={{ 
            mb: subtitle ? 0.5 : 0,
            fontWeight: 600,
            textAlign: isCompact ? 'center' : 'left',
            fontSize: isCompact ? '0.9rem' : '1.1rem'
          }}
        >
          {title}
        </Typography>
        
        {subtitle && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              textAlign: isCompact ? 'center' : 'left',
              fontSize: isCompact ? '0.75rem' : '0.875rem',
              fontStyle: 'italic'
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default StatsCard;
